"use client";

import type { MediaSliderBlock } from "@mw/shared/payload-types";

import { formatLinks, getLinkProps, getVideoUrl } from "@/app/_utilities/payload";
import SliderExpressive from "@components/composers/SliderExpressiveWrapper";
import { AspectRatio } from "@components/core/AspectRatio";
import { HairlineCardBorder } from "@components/core/HairlineCardBorder";
import { Image } from "@components/core/Image";
import LazyVideoPlayer from "@components/core/LazyVideoPlayer";
import { Link } from "@components/core/Link";
import { ImageSizes } from "@ui/lib/image";
import { cn, pxToRem } from "@ui/lib/utils";

import { Block } from "./Block";
import { bentoCardVariants } from "./BlockBentoGrid/sharedCardVariants";
import { OrbWithMessages } from "@ui/components/core/Orb/OrbWithMessages";
import { useTranslations } from "next-intl";
import clsx from "clsx";
import type { ReactNode } from "react";
import { ScrollGrid } from "@ui/components/layout/ScrollGrid";

interface AssetProps {
  asset: NonNullable<MediaSliderBlock["assets"]>[number] & { assetTitle?: ReactNode | string | undefined | null };
  aspectRatio: MediaSliderBlock["aspectRatio"];
  hasBackgroundCard: boolean;
  isGridLayout: boolean;
  isCenter?: boolean;
  animateCenter?: boolean;
}

const ImageContent = ({
  asset,
  aspectRatio,
  hasBackgroundCard,
  isGridLayout,
  formattedLink,
}: AssetProps & { formattedLink?: ReturnType<typeof getLinkProps> }) => {
  const t = useTranslations("ClientKeys.BlockMediaSlider");
  const _image = {
    resource: asset.image,
    sizes: ImageSizes.FULL_HALF_ON_LG,
  };
  switch (asset.assetType) {
    case "orb_demo":
      return (
        <AspectRatio
          border={!asset.hideBorder && !isGridLayout}
          ratio={aspectRatio ?? "1x1"}
          rounded={!hasBackgroundCard}
          className={formattedLink ? "group-hover/card:tw-border-black tw-transition-all tw-duration-300" : ""}
        >
          <div className='tw-flex tw-size-full tw-justify-center tw-py-1 tw-items-center tw-bg-cream'>
            <div className='tw-relative tw-size-48'>
              <OrbWithMessages
                messages={[
                  {
                    id: 1,
                    role: "agent",
                    message: t("orb_demo.orb_message"),
                  },
                ]}
                animated={false}
                image={{ sizes: pxToRem(192) }}
                texture={asset.orbTexture ?? "agents-1"}
                messagesClassName='tw-opacity-0 group-hover/card:tw-opacity-100 tw-transition-opacity tw-duration-300'
                alignMessages='top'
              />
            </div>
          </div>
        </AspectRatio>
      );
    case "video":
      const videoUrl = getVideoUrl(asset.video);
      return (
        <LazyVideoPlayer
          cover={_image}
          ratio={aspectRatio ?? "1x1"}
          src={videoUrl!}
          transparentButton
          withPlayButtonLabels={false}
        />
      );
    case "image":
      return (
        <AspectRatio
          border={!asset.hideBorder && !isGridLayout}
          ratio={aspectRatio ?? "1x1"}
          rounded={!hasBackgroundCard}
        >
          <Image {..._image} className='slider-image' sizes={ImageSizes.FULL_1_3_ON_LG} />
          {asset.showInOverlay && (
            <div className='tw-absolute tw-inset-0 tw-flex tw-flex-col tw-justify-end tw-p-6'>
              <p className='f-paragraph-01 text-inverse'>{asset.assetTitle}</p>
              <p className='tw-line-clamp-3 f-paragraph-03 text-inverse'>{asset.assetDescription}</p>
            </div>
          )}
        </AspectRatio>
      );
  }
};

const Asset = ({
  asset,
  aspectRatio,
  hasBackgroundCard,
  isGridLayout,
  isCenter = false,
  animateCenter = false,
}: AssetProps) => {
  const { assetTitle, assetDescription, showInOverlay, metaBoxStyle = "default", link } = asset;
  const formattedLink = getLinkProps(link);
  const linkLabel =
    typeof formattedLink?.children === "string" && formattedLink.children.trim().length > 0
      ? formattedLink.children
      : assetTitle || "Open media slider item";

  const metaContent = !showInOverlay && (assetTitle || assetDescription) && (
    <>
      <div
        className={cn(
          "tw-relative tw-z-10 tw-flex tw-flex-1 tw-flex-col tw-justify-between tw-gap-1",
          hasBackgroundCard ? "tw-p-8 tw-bg-cream" : "tw-mt-5",
          metaBoxStyle === "default" ? "tw-text-center" : "tw-text-left tw-px-3",
        )}
      >
        {assetTitle && (
          <h4 className={clsx(metaBoxStyle === "default" ? "f-ui-05" : "f-paragraph-01")}>{assetTitle}</h4>
        )}
        {assetDescription && (
          <p
            className={cn(
              "tw-line-clamp-3 text-light",
              metaBoxStyle === "default" ? "f-ui-05" : "tw-text-balance f-paragraph-03",
            )}
          >
            {assetDescription}
          </p>
        )}
      </div>

      {formattedLink && (
        <Link {...formattedLink} aria-label={linkLabel} className='tw-outline-none' data-card-link>
          <span aria-hidden className='tw-absolute tw-inset-0 tw-z-30' />
          <span className='tw-sr-only'>{linkLabel}</span>
        </Link>
      )}
    </>
  );

  // Grid layout with vertical lines running behind image
  if (isGridLayout) {
    return (
      <div
        className={cn(
          "tw-relative tw-flex tw-h-full tw-flex-col tw-group/card",
          hasBackgroundCard && bentoCardVariants({ background: "default" }),
          animateCenter && "tw-transition-transform tw-duration-500 tw-ease-in-out",
          animateCenter && (isCenter ? "lg:tw-scale-100" : "lg:tw-scale-95"),
        )}
      >
        {/* Vertical grid lines - absolutely positioned to run full height */}
        <div className='tw-absolute tw-inset-0 tw-z-0 tw-pointer-events-none'>
          <div className='tw-absolute tw-left-14 tw-top-0 tw-bottom-0 tw-w-px tw-bg-black/7.5' />
          <div className='tw-absolute tw-right-14 tw-top-0 tw-bottom-0 tw-w-px tw-bg-black/7.5' />
        </div>

        {/* Grid container with padding on top/sides */}
        <div className='tw-flex tw-flex-col tw-flex-1'>
          <div className='tw-h-16' />

          {/* Horizontal line at top of image */}
          <div className='tw-relative'>
            <div className='tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-z-0 tw-h-px tw-bg-black/7.5 tw-translate-y-full' />
            <div className='tw-relative tw-z-10 tw-px-14 tw-pt-px'>
              <ImageContent
                asset={asset}
                aspectRatio={aspectRatio}
                hasBackgroundCard={hasBackgroundCard}
                isGridLayout={isGridLayout}
                isCenter={isCenter}
                animateCenter={animateCenter}
                formattedLink={formattedLink}
              />
            </div>
            <div className='tw-absolute tw-left-0 tw-right-0 tw-bottom-0 tw-z-0 tw-h-px tw-bg-black/7.5' />
          </div>
        </div>
        {metaContent}
        {hasBackgroundCard && <HairlineCardBorder />}
      </div>
    );
  }

  // Default card style
  return (
    <div
      className={cn(
        "tw-relative tw-flex tw-h-full tw-flex-col tw-group/card",
        hasBackgroundCard && bentoCardVariants({ background: "default" }),
        animateCenter && "tw-transition-transform tw-duration-500 tw-ease-in-out",
        animateCenter && (isCenter ? "lg:tw-scale-100" : "lg:tw-scale-95"),
      )}
    >
      <div className={cn("tw-flex-1", hasBackgroundCard && "tw-overflow-hidden tw-rounded-2xl")}>
        <ImageContent
          asset={asset}
          aspectRatio={aspectRatio}
          hasBackgroundCard={hasBackgroundCard}
          isGridLayout={isGridLayout}
          isCenter={isCenter}
          animateCenter={animateCenter}
          formattedLink={formattedLink}
        />
      </div>
      {metaContent}
      {hasBackgroundCard && <HairlineCardBorder />}
    </div>
  );
};

const BlockMediaSlider = ({
  assets,
  links,
  variant,
  aspectRatio,
  cardStyle = "default",
  imageLayout = "full",
}: MediaSliderBlock) => {
  const _links = formatLinks(links || []);

  const hasBackgroundCard = cardStyle === "background-with-border";
  // Grid layout only works with background card style
  const isGridLayout = imageLayout === "grid" && hasBackgroundCard;

  if (!assets || assets.length === 0) return null;

  const _assets = assets.map((asset, assetIndex) => (
    <Asset
      key={assetIndex}
      animateCenter={false}
      aspectRatio={aspectRatio}
      asset={asset}
      hasBackgroundCard={hasBackgroundCard}
      isGridLayout={isGridLayout}
    />
  ));

  let alignNavButtonsToRatio: number | undefined = undefined;
  if (aspectRatio) {
    alignNavButtonsToRatio = Number(aspectRatio.split("x")[0]) / Number(aspectRatio.split("x")[1]);
    if (isNaN(alignNavButtonsToRatio)) {
      alignNavButtonsToRatio = undefined;
    }
  }

  return (
    <Block name='tabbed-asset-slider'>
      {variant === "expressive" ? (
        <SliderExpressive className='' items={_assets} />
      ) : (
        <ScrollGrid items={_assets} renderItem={asset => asset} alignNavButtonsToRatio={alignNavButtonsToRatio} />
      )}
      <div className='tw-mt-8 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 lg:tw-flex-row container'>
        {_links.map((link, linkIndex) => (
          <Link {...link} asButton buttonVariants={{ variant: "primary" }} key={linkIndex} />
        ))}
      </div>
    </Block>
  );
};

BlockMediaSlider.displayName = "BlockMediaSlider";

export { BlockMediaSlider };
